import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Collapse, Divider, Skeleton, Stack } from '@mui/material';

import { themeColors } from 'colors';
import {
    formatModuleName,
    getCascadeRemovedPermissionIds,
    getPermissionModuleKeysInOrder,
    getTransitiveDependencyIds,
    groupPermissionsByModule,
} from 'utils';

import CrossModuleAutoEnableAlert from './CrossModuleAutoEnableAlert';
import PermissionModuleHeader from './PermissionModuleHeader';
import PermissionRow from './PermissionRow';

import type { AccessCentrePermission } from 'domains/accessCentre/types';

const CROSS_MODULE_AUTO_ENABLE_ALERT_PREFIX = 'Some permissions here might have auto-enabled permissions in ';

/** Stable DOM id for scrolling to a module section (`scrollIntoView`). */
const getRoleBuilderModuleAnchorId = (moduleKey: string) =>
    `role-builder-module-${moduleKey.replace(/[^a-zA-Z0-9_-]/g, '_')}`;

/** Resolved expand state: explicit user toggle wins; otherwise default expanded when "show enabled only" filters the list. */
const getModuleExpanded = (
    moduleKey: string,
    showEnabledOnly: boolean,
    expandedModules: Record<string, boolean>
): boolean => {
    const stored = expandedModules[moduleKey];
    if (stored !== undefined) {
        return stored;
    }
    return showEnabledOnly;
};

const expandedPanelSx = {
    bgcolor: themeColors.roleBuilder.moduleExpandedBackground,
    borderRadius: 1,
    overflow: 'hidden',
} as const;

/** Expanded panel when a cross-module auto-enable alert sits above it. */
const expandedPanelWithAlertMarginSx = {
    ...expandedPanelSx,
    mt: 1,
} as const;

const skeletonModuleHeaderRowSx = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    columnGap: 2,
    rowGap: 1,
    py: 1,
} as const;

const permissionListingRootStackSx = { width: '100%' } as const;

const skeletonExpandedPanelWrapSx = { pt: 0.5 } as const;

const skeletonPermissionRowSx = { borderRadius: 0 } as const;

const moduleSectionDividerSx = {
    borderColor: themeColors.roleBuilder.moduleSectionDivider,
    mt: 1,
} as const;

const moduleAnchorBoxSx = { scrollMarginTop: 2 } as const;

const collapseInnerBoxSx = { width: '100%', pt: 0.5 } as const;

const PERMISSION_LISTING_SKELETON_MODULE_COUNT = 3;
const PERMISSION_LISTING_SKELETON_ROWS_PER_MODULE = 3;

export type PermissionListingProps = {
    permissions: AccessCentrePermission[];
    /**
     * When set (e.g. content-only UI), only permissions whose `AccessCentrePermission.module` matches are listed.
     * Pass a single module or an allowlist (e.g. Content + Access Centre).
     * Transitive deps and cascade removal still use the full `permissions` catalog.
     */
    restrictToPermissionModule?: string | readonly string[];
    selectedPermissionIds: number[];
    manuallyEnabledPermissionIds: Set<number>;
    onSelectionChange: (permissionIds: number[]) => void;
    onManuallyEnabledChange: (ids: Set<number>) => void;
    showEnabledOnly: boolean;
    isLoading?: boolean;
    readOnly: boolean;
};

const PermissionListing = ({
    permissions,
    restrictToPermissionModule,
    selectedPermissionIds,
    manuallyEnabledPermissionIds,
    onSelectionChange,
    onManuallyEnabledChange,
    showEnabledOnly,
    isLoading = false,
    readOnly,
}: PermissionListingProps) => {
    const selectedSet = useMemo(() => new Set(selectedPermissionIds), [selectedPermissionIds]);

    const permissionsInScope = useMemo(() => {
        if (restrictToPermissionModule == null || restrictToPermissionModule === '') return permissions;
        const allow = Array.isArray(restrictToPermissionModule)
            ? restrictToPermissionModule
            : [restrictToPermissionModule];
        return permissions.filter((p) => allow.includes(p.module));
    }, [permissions, restrictToPermissionModule]);

    const permissionsByModule = useMemo(() => groupPermissionsByModule(permissionsInScope), [permissionsInScope]);
    const moduleKeys = useMemo(() => getPermissionModuleKeysInOrder(permissionsInScope), [permissionsInScope]);

    /** Display names (sorted) of permissions auto-enabled when enabling each permission (transitive deps). */
    const autoEnabledDependencyNamesByPermissionId = useMemo(() => {
        const map = new Map<number, string[]>();
        const byId = new Map(permissions.map((p) => [p.id, p]));
        for (const p of permissions) {
            if (!p.depends_on?.length) continue;
            const ids = getTransitiveDependencyIds(permissions, p.id);
            const names = ids
                .map((id) => byId.get(id)?.name)
                .filter((n): n is string => Boolean(n))
                .sort((a, b) => a.localeCompare(b));
            if (names.length > 0) {
                map.set(p.id, names);
            }
        }
        return map;
    }, [permissions]);

    const [expandedModules, setExpandedModules] = useState<Record<string, boolean>>({});

    const prevShowEnabledOnlyRef = useRef(showEnabledOnly);

    useEffect(() => {
        const turnedOn = showEnabledOnly && !prevShowEnabledOnlyRef.current;
        prevShowEnabledOnlyRef.current = showEnabledOnly;

        if (!turnedOn) {
            return;
        }

        setExpandedModules((prev) => {
            const next = { ...prev };
            for (const key of moduleKeys) {
                const perms = permissionsByModule.get(key) ?? [];
                if (perms.some((p) => selectedSet.has(p.id))) {
                    next[key] = true;
                }
            }
            return next;
        });
    }, [showEnabledOnly, moduleKeys, permissionsByModule, selectedSet]);

    const scrollToModule = useCallback((targetKey: string) => {
        setExpandedModules((prev) => ({ ...prev, [targetKey]: true }));
        window.setTimeout(() => {
            document.getElementById(getRoleBuilderModuleAnchorId(targetKey))?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        }, 350);
    }, []);

    const toggleExpanded = (moduleKey: string) => {
        setExpandedModules((prev) => {
            const current = getModuleExpanded(moduleKey, showEnabledOnly, prev);
            return { ...prev, [moduleKey]: !current };
        });
    };

    const applySelectionAndManual = (nextSelected: Set<number>, nextManual: Set<number>) => {
        onSelectionChange(Array.from(nextSelected));
        onManuallyEnabledChange(nextManual);
    };

    const togglePermission = (id: number) => {
        const selected = new Set(selectedSet);
        const manual = new Set(manuallyEnabledPermissionIds);

        if (selected.has(id)) {
            const toRemove = getCascadeRemovedPermissionIds(permissions, selected, [id]);
            Array.from(toRemove).forEach((rid) => {
                selected.delete(rid);
                manual.delete(rid);
            });
        } else {
            selected.add(id);
            manual.add(id);
            const dependencyIds = getTransitiveDependencyIds(permissions, id);
            for (const depId of dependencyIds) {
                selected.add(depId);
            }
        }
        applySelectionAndManual(selected, manual);
    };

    const toggleModuleAll = (moduleKey: string, checked: boolean) => {
        const modulePermissions = permissionsByModule.get(moduleKey) ?? [];
        const selected = new Set(selectedSet);
        const manual = new Set(manuallyEnabledPermissionIds);

        if (checked) {
            for (const p of modulePermissions) {
                selected.add(p.id);
                manual.add(p.id);
                for (const depId of getTransitiveDependencyIds(permissions, p.id)) {
                    selected.add(depId);
                }
            }
        } else {
            const initialRemoved = modulePermissions.map((p) => p.id).filter((pid) => selected.has(pid));
            const toRemove = getCascadeRemovedPermissionIds(permissions, selected, initialRemoved);
            Array.from(toRemove).forEach((rid) => {
                selected.delete(rid);
                manual.delete(rid);
            });
        }
        applySelectionAndManual(selected, manual);
    };

    const isAutoEnabled = (permissionId: number): boolean =>
        selectedSet.has(permissionId) && !manuallyEnabledPermissionIds.has(permissionId);

    const visibleModuleKeys = useMemo(() => {
        if (!showEnabledOnly) return moduleKeys;
        return moduleKeys.filter((key) => {
            const perms = permissionsByModule.get(key) ?? [];
            return perms.some((p) => selectedSet.has(p.id));
        });
    }, [moduleKeys, permissionsByModule, selectedSet, showEnabledOnly]);

    if (isLoading) {
        return (
            <Stack
                data-testid="access-center-permission-listing"
                sx={permissionListingRootStackSx}
                role="progressbar"
                aria-label="Loading permissions"
                spacing={0}
            >
                {Array.from({ length: PERMISSION_LISTING_SKELETON_MODULE_COUNT }, (_, moduleIndex) => (
                    <Box key={moduleIndex}>
                        <Box sx={skeletonModuleHeaderRowSx}>
                            <Skeleton variant="text" width="42%" height={28} />
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Skeleton variant="rounded" width={100} height={22} />
                                <Skeleton variant="rounded" width={44} height={36} />
                                <Skeleton variant="circular" width={40} height={40} />
                            </Stack>
                        </Box>
                        <Box sx={skeletonExpandedPanelWrapSx}>
                            <Box sx={expandedPanelSx}>
                                <Stack spacing={0}>
                                    {Array.from(
                                        { length: PERMISSION_LISTING_SKELETON_ROWS_PER_MODULE },
                                        (_, rowIndex) => (
                                            <Skeleton
                                                key={rowIndex}
                                                variant="rectangular"
                                                height={68}
                                                sx={skeletonPermissionRowSx}
                                            />
                                        )
                                    )}
                                </Stack>
                            </Box>
                        </Box>
                        <Divider sx={moduleSectionDividerSx} />
                    </Box>
                ))}
            </Stack>
        );
    }

    return (
        <Stack spacing={0} data-testid="access-center-permission-listing" sx={permissionListingRootStackSx}>
            {visibleModuleKeys.map((moduleKey) => {
                const modulePermissions = permissionsByModule.get(moduleKey) ?? [];
                const visiblePermissions = showEnabledOnly
                    ? modulePermissions.filter((p) => selectedSet.has(p.id))
                    : modulePermissions;

                const allInModuleSelected =
                    modulePermissions.length > 0 && modulePermissions.every((p) => selectedSet.has(p.id));

                const totalInModule = modulePermissions.length;
                const selectedInModule = modulePermissions.filter((p) => selectedSet.has(p.id)).length;
                const autoEnabledInModule = modulePermissions.filter((p) => isAutoEnabled(p.id)).length;

                const isExpanded = getModuleExpanded(moduleKey, showEnabledOnly, expandedModules);

                const prerequisiteIdsFromManualInThisModule = new Set<number>();
                for (const p of modulePermissions) {
                    if (!selectedSet.has(p.id) || !manuallyEnabledPermissionIds.has(p.id)) continue;
                    getTransitiveDependencyIds(permissions, p.id).forEach((id) => {
                        prerequisiteIdsFromManualInThisModule.add(id);
                    });
                }
                const otherModuleKeysWithCrossAutoDeps = new Set<string>();
                for (const perm of permissions) {
                    if (perm.module === moduleKey) continue;
                    if (!selectedSet.has(perm.id) || !isAutoEnabled(perm.id)) continue;
                    if (prerequisiteIdsFromManualInThisModule.has(perm.id)) {
                        otherModuleKeysWithCrossAutoDeps.add(perm.module);
                    }
                }
                const crossModuleLinkedModules =
                    otherModuleKeysWithCrossAutoDeps.size > 0
                        ? Array.from(otherModuleKeysWithCrossAutoDeps)
                              .sort()
                              .map((k) => ({ moduleKey: k, label: formatModuleName(k) }))
                        : [];

                return (
                    <Box
                        key={moduleKey}
                        id={getRoleBuilderModuleAnchorId(moduleKey)}
                        data-testid={`access-center-permissions-module-${moduleKey}`}
                        sx={moduleAnchorBoxSx}
                    >
                        <PermissionModuleHeader
                            moduleKey={moduleKey}
                            moduleTitle={formatModuleName(moduleKey)}
                            selectedInModule={selectedInModule}
                            totalInModule={totalInModule}
                            autoEnabledInModule={autoEnabledInModule}
                            allInModuleSelected={allInModuleSelected}
                            isExpanded={isExpanded}
                            readOnly={readOnly}
                            onToggleExpand={() => toggleExpanded(moduleKey)}
                            onToggleModuleAll={(checked) => toggleModuleAll(moduleKey, checked)}
                        />
                        <Collapse in={isExpanded}>
                            <Box sx={collapseInnerBoxSx}>
                                <CrossModuleAutoEnableAlert
                                    prefix={CROSS_MODULE_AUTO_ENABLE_ALERT_PREFIX}
                                    linkedModules={crossModuleLinkedModules}
                                    onModuleClick={scrollToModule}
                                    data-testid={`access-center-module-cross-deps-alert-${moduleKey}`}
                                />
                                <Box
                                    sx={
                                        crossModuleLinkedModules.length > 0
                                            ? expandedPanelWithAlertMarginSx
                                            : expandedPanelSx
                                    }
                                >
                                    <Stack spacing={0}>
                                        {visiblePermissions.map((permission) => (
                                            <PermissionRow
                                                key={permission.id}
                                                permission={permission}
                                                autoEnabledDependencyNames={
                                                    autoEnabledDependencyNamesByPermissionId.get(permission.id) ?? []
                                                }
                                                isOn={selectedSet.has(permission.id)}
                                                showAutoEnabledTag={isAutoEnabled(permission.id)}
                                                onToggle={togglePermission}
                                                readOnly={readOnly}
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                            </Box>
                        </Collapse>
                        <Divider sx={moduleSectionDividerSx} />
                    </Box>
                );
            })}
        </Stack>
    );
};

export default PermissionListing;
